// Third Party
import * as React from 'react';
import cx from 'classnames';
import dynamic from 'next/dynamic';
import times from 'lodash/times';
import { Wrapper } from '@googlemaps/react-wrapper';
import { debounce, toInteger } from 'lodash';
import moment from 'moment';

// Utils
import getConfig from '@config';
import { usePlaceFindByIdQuery } from '@gql';
import { ListingsArray } from '../types';
import { concatenateAndReplace } from '@utils/test-helpers';
import { formatDateYYYYMMDD } from '@utils/date-helpers';
import { routes } from '@routes';
import { scrollToTop } from '@utils/scroll-to-top';
import { useMap } from '@components/map/map-context';
import { searchPageSize, useSearch } from '@contexts/search-context';
import { formatWholeDollarAmount } from '@utils/money';

// UI
import palette from '../../../../color-palette.json';
import { AnalyticsRef } from '@components/analytics-ref';
import { Button } from '@components/button';
import { Divider } from '@components/divider';
import { FeaturesModal } from './features-modal';
import { IconNames } from '@components/icon';
import { Link } from '@components/link';
import { ListingMapCard, ListingPartial } from './listing-map-card';
import { Map, MapMarker, MapMarkerDefaultIcon } from '@components/map';
import { MobileSearchModal } from '@components/mobile-search-modal';
import { ListingCard } from '@components/listing-card';
import { Typography } from '@components/typography';
import { ButtonIcon } from '@components/button-icon';
import { extractFirstPhraseBeforeComma } from '@utils/extract-first-phrase-before-comma';
import { guestDescription } from '@utils/guest-description';

type Props = {};

/**
 * This module relies on the class google.maps.OverlayView which
 * is only available on the window once the google maps script has loaded.
 */
const MapOverlay = dynamic(() => import('./map-overlay'), {
  ssr: false,
});

// Add this custom hook before the SearchMap component
const useScrollPosition = (
  containerRef: React.RefObject<HTMLDivElement | null>,
  isListActive: boolean
) => {
  const [_savedPosition, setSavedPosition] = React.useState(0);

  // Save position to both state and session storage
  const savePosition = React.useCallback((position: number) => {
    setSavedPosition(position);
    sessionStorage.setItem('searchResultsScrollPosition', position.toString());
  }, []);

  // Handle scroll events
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const position = container.scrollTop;
      if (position > 0) {
        savePosition(position);
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [containerRef, savePosition]);

  // Restore position when list becomes active
  React.useEffect(() => {
    if (isListActive && containerRef.current) {
      const savedPos = sessionStorage.getItem('searchResultsScrollPosition');
      if (savedPos) {
        const position = parseInt(savedPos);
        if (position > 0) {
          requestAnimationFrame(() => {
            if (
              containerRef.current &&
              position <= containerRef.current.scrollHeight
            ) {
              containerRef.current.scrollTop = position;
            }
          });
        }
      }
    }
  }, [isListActive, containerRef]);

  // Clear saved position on new search
  React.useEffect(() => {
    const isNewSearch =
      (
        window.performance.getEntriesByType(
          'navigation'
        )[0] as PerformanceNavigationTiming
      ).type === 'navigate';
    if (isNewSearch) {
      sessionStorage.removeItem('searchResultsScrollPosition');
      setSavedPosition(0);
      if (containerRef.current) {
        containerRef.current.scrollTop = 0;
      }
    }
  }, []);

  return { savePosition, setSavedPosition };
};

export const SearchMap: React.FC<Props> = () => {
  const {
    publicRuntimeConfig: {
      google: { apiKey },
    },
  } = getConfig();

  const {
    activateFeaturesModal,
    activateMobileSearchModal,
    activeFilters,
    adultCount,
    centerPoint,
    checkInDate,
    checkOutDate,
    childCount,
    infantCount,
    listingsLoading,
    listingsQueryResults,
    mapZoom,
    petCount,
    placeId,
    placeName: _placeName,
    geoSearchBox,
    resetPagination,
    setActiveFilters,
    setAdultCount,
    setCheckInDate,
    setCheckOutDate,
    setChildCount,
    setInfantCount,
    setMapGeoSearchBox,
    setPetCount,
    nextPage,
    prevPage,
    propertyTypes,
    setPropertyTypes,
    uniquePropertyTypes,
    setUniquePropertyTypes,
    minNumberOfRooms,
    setMinNumberOfRooms,
    minNumberOfBathrooms,
    setMinNumberOfBathrooms,
    minPrice,
    maxPrice,
    setMinPrice,
    setMaxPrice,
    searchType,
  } = useSearch();

  const { map } = useMap();

  // Constants
  const minAcceptableZoom = 10;

  // All hooks must be at the top level, before any conditional logic
  const resultsContainerRef = React.useRef<HTMLDivElement>(null);
  const [isContentReady, setIsContentReady] = React.useState(false);
  const [isListActive, setIsListActive] = React.useState<boolean>(true);
  const [hoveredListingSlug, setHoveredListingSlug] = React.useState<
    string | undefined
  >(undefined);
  const [selectedListingId, setSelectedListingId] = React.useState<
    string | undefined
  >(undefined);
  const [selectedListing, setSelectedListing] = React.useState<
    ListingPartial | undefined
  >(undefined);
  const [zoomingOutToNewListing, setZoomingOutToNewListing] =
    React.useState(false);
  const [preZoomMapListingCount, setPreZoomMapListingCount] = React.useState(0);
  const [isAboveMinZoom, setIsAboveMinZoom] = React.useState(false);
  const { savePosition, setSavedPosition } = useScrollPosition(
    resultsContainerRef,
    isListActive
  );
  // Memoize listingEntries
  const memoizedListingEntries = React.useMemo(() => {
    if (
      listingsQueryResults?.listingsSearch.__typename ===
      'ListingSearchCursorCollection'
    ) {
      return listingsQueryResults.listingsSearch.entries;
    }
    return [];
  }, [listingsQueryResults]);

  // Update the saveScrollPosition callback
  const saveScrollPosition = React.useCallback(() => {
    const container = resultsContainerRef.current;
    if (container) {
      savePosition(container.scrollTop);
    }
  }, [savePosition]);

  // Set content ready when results are loaded
  React.useEffect(() => {
    if (!listingsLoading && listingsQueryResults) {
      setIsContentReady(true);
    }
  }, [listingsLoading, listingsQueryResults]);

  // Initialize state from session storage
  React.useEffect(() => {
    const savedIsListActive = sessionStorage.getItem('isListActive');
    const savedSelectedListingId = sessionStorage.getItem('selectedListingId');
    const savedScrollPos = sessionStorage.getItem(
      'searchResultsScrollPosition'
    );

    if (savedIsListActive) {
      setIsListActive(JSON.parse(savedIsListActive));
    }
    if (savedSelectedListingId) {
      setSelectedListingId(savedSelectedListingId);
    }
    if (savedScrollPos) {
      const position = parseInt(savedScrollPos);

      // Set content ready when results are loaded
      setSavedPosition(position);
    }
  }, [listingsLoading, listingsQueryResults]);

  // Handle state persistence and restoration
  React.useEffect(() => {
    const container = resultsContainerRef.current;
    if (!container || !isContentReady) return;

    const isNewSearch =
      (
        window.performance.getEntriesByType(
          'navigation'
        )[0] as PerformanceNavigationTiming
      ).type === 'navigate';

    if (isNewSearch) {
      sessionStorage.removeItem('searchResultsScrollPosition');
      sessionStorage.removeItem('isListActive');
      sessionStorage.removeItem('selectedListingId');
      container.scrollTop = 0;
      setIsListActive(true);
      setSelectedListingId(undefined);
    } else {
      const savedPosition = sessionStorage.getItem(
        'searchResultsScrollPosition'
      );

      if (savedPosition) {
        const position = parseInt(savedPosition);
        if (position >= 0) {
          requestAnimationFrame(() => {
            if (container && position <= container.scrollHeight) {
              container.scrollTop = position;
            }
          });
        }
      }
    }
  }, [isContentReady]);

  // Update selected listing when ID changes
  React.useEffect(() => {
    if (selectedListingId) {
      const listing = memoizedListingEntries.find(
        (listing) => listing.id === selectedListingId
      );
      setSelectedListing(listing);
      sessionStorage.setItem('selectedListingId', selectedListingId);
    } else {
      setSelectedListing(undefined);
      sessionStorage.removeItem('selectedListingId');
    }
  }, [selectedListingId, memoizedListingEntries]);

  // Persist list active state
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('isListActive', isListActive.toString());
    }
  }, [isListActive]);

  const onMapCardCloseHandler = React.useCallback(() => {
    setSelectedListingId(undefined);
  }, []);

  // Update pre-zoom listing count
  React.useEffect(() => {
    if (listingsLoading) return;
    const newListingsFound =
      memoizedListingEntries.length > preZoomMapListingCount;
    if (!newListingsFound && zoomingOutToNewListing) {
      zoomOutToNewListing(preZoomMapListingCount);
    } else {
      setPreZoomMapListingCount(memoizedListingEntries.length);
      setZoomingOutToNewListing(false);
    }
  }, [
    memoizedListingEntries,
    listingsLoading,
    zoomingOutToNewListing,
    preZoomMapListingCount,
  ]);

  // Update map zoom level
  React.useEffect(() => {
    setIsAboveMinZoom(
      (map?.getZoom() || minAcceptableZoom) > minAcceptableZoom
    );
  }, [map, minAcceptableZoom]);

  // Map change handler
  const mapChangeHandler = React.useCallback(
    (map: google.maps.Map) => {
      const bounds = map.getBounds();
      const zoom = map.getZoom();

      setIsAboveMinZoom((zoom || minAcceptableZoom) > minAcceptableZoom);

      if (zoom) {
        var queryParams = new URLSearchParams(window.location.search);
        queryParams.set('zoom', zoom.toString());
        history.replaceState(null, '', '?' + queryParams.toString());
      }

      const centerPointLat = bounds?.getCenter().lat();
      const centerPointLng = bounds?.getCenter().lng();
      const southWestPointLat = bounds?.getSouthWest().lat();
      const southWestPointLng = bounds?.getSouthWest().lng();
      const northEastPointLat = bounds?.getNorthEast().lat();
      const northEastPointLng = bounds?.getNorthEast().lng();

      if (
        centerPointLat &&
        centerPointLng &&
        southWestPointLat &&
        southWestPointLng &&
        northEastPointLat &&
        northEastPointLng
      ) {
        resetPagination();
        scrollToTop();
        setMapGeoSearchBox({
          southWestPoint: {
            lat: southWestPointLat,
            lng: southWestPointLng,
          },
          northEastPoint: {
            lat: northEastPointLat,
            lng: northEastPointLng,
          },
          centerPoint: {
            lat: centerPointLat,
            lng: centerPointLng,
          },
        });
      }
    },
    [resetPagination, setMapGeoSearchBox, minAcceptableZoom]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedMapChangeHandler = React.useCallback(
    debounce((map: google.maps.Map) => mapChangeHandler(map), 800),
    []
  );
  // Show content only when ready
  const shouldShowContent = isContentReady;

  const totalCount =
    (listingsQueryResults?.listingsSearch.__typename ===
      'ListingSearchCursorCollection' &&
      listingsQueryResults?.listingsSearch.metadata.totalCount) ||
    0;
  const listingEntries =
    (listingsQueryResults?.listingsSearch.__typename ===
      'ListingSearchCursorCollection' &&
      listingsQueryResults?.listingsSearch.entries) ||
    [];

  const hasFiltersApplied =
    activeFilters.length > 0 ||
    minNumberOfBathrooms > 0 ||
    minNumberOfRooms ||
    propertyTypes.length > 0 ||
    uniquePropertyTypes.length > 0 ||
    minPrice ||
    maxPrice;

  const clearDates = (): void => {
    setCheckInDate(null);
    setCheckOutDate(null);
  };

  const resetFilters = (): void => {
    setPropertyTypes([]);
    setUniquePropertyTypes([]);
    setActiveFilters([]);
    setMinNumberOfRooms(0);
    setMinNumberOfBathrooms(0);
    setAdultCount(1);
    setInfantCount(0);
    setChildCount(0);
    setPetCount(0);
    setMinPrice(null);
    setMaxPrice(null);
  };

  const { data } = usePlaceFindByIdQuery({
    variables: {
      id: placeId ? placeId : '',
    },
  });

  const [searchedCountry, setSearchedCountry] = React.useState<
    string | undefined
  >(undefined);

  React.useEffect(() => {
    if (data?.placeById.__typename === 'Place') {
      setSearchedCountry(data.placeById?.countryOrRegionShortName);
    }
  }, [data?.placeById]);
  // Is it out of scope for this to change if they scroll from canada/mexico to usa?

  const [mapListings, setMapListings] = React.useState<ListingsArray>([]);
  const [placeName, setPlaceName] = React.useState<string | undefined>(
    _placeName || ''
  );

  React.useEffect(() => {
    if (
      listingsQueryResults?.listingsSearch?.__typename ===
      'ListingSearchCursorCollection'
    ) {
      setMapListings(listingsQueryResults?.listingsSearch.entries);

      // validate whether or not the selected listing is in listings shown on the map
      // if the selected listing is not in the listings shown on the map, set it to undefined
      if (
        selectedListing &&
        !listingsQueryResults?.listingsSearch.entries
          .map((listing) => listing.id)
          .includes(selectedListing.id)
      ) {
        setSelectedListingId(undefined);
      }

      setPlaceName(
        listingsQueryResults?.listingsSearch.searchPlace?.name || ''
      );
    }
  }, [listingsQueryResults]);

  const updateSelectedListing = (
    listing: ListingPartial,
    isFavorited: boolean
  ) => {
    setSelectedListing({ ...listing, isFavorited });
  };

  const mapClicked = React.useCallback((_event: google.maps.MapMouseEvent) => {
    setSelectedListingId(undefined);
  }, []);

  const zoomOutToNewListing = React.useCallback(
    (previousListingCount: number) => {
      if (listingsLoading) return;
      const currentZoom = map?.getZoom();
      const canZoomOut = currentZoom && currentZoom > minAcceptableZoom;
      const newListingsFound = listingEntries.length > previousListingCount;
      if (canZoomOut && !newListingsFound) {
        map?.setZoom(currentZoom - 1);
      } else {
        setZoomingOutToNewListing(false);
      }
    },
    [map, listingEntries, listingsLoading]
  );

  if (
    listingsQueryResults?.listingsSearch.__typename ===
    'ValidationErrorCollection'
  ) {
    return <div>Something went wrong</div>;
  }

  const propertyCountFormattedString = (count: number) => {
    if (count == 1) return '1 place';
    if (count == 0) return 'No exact matches';
    if (count < 1000) return count + ' places';

    return 'Over 1,000 places';
  };

  return (
    <div className="flex w-full h-full sm:max-h-[calc(100vh-178px)] bg-white">
      <FeaturesModal
        viewAllProperties={(
          propertyTypes,
          uniquePropertyTypes,
          minNumberOfRooms,
          minNumberOfBathrooms,
          minPrice,
          maxPrice,
          filters
        ) => {
          setMinNumberOfBathrooms(minNumberOfBathrooms);
          setMinNumberOfRooms(minNumberOfRooms);
          setPropertyTypes(propertyTypes);
          setUniquePropertyTypes(uniquePropertyTypes);
          setMinPrice(minPrice);
          setMaxPrice(maxPrice);
          setActiveFilters(filters);
          resetPagination();
        }}
      />

      <MobileSearchModal
        placeName={placeName || undefined}
        placeId={placeId || undefined}
        checkInDate={checkInDate}
        checkOutDate={checkOutDate}
        adultCount={adultCount}
        childCount={childCount}
        infantCount={infantCount}
        petCount={petCount}
        mapZoom={mapZoom}
        geoSearchBox={geoSearchBox}
        searchType={searchType}
        activeFilters={activeFilters}
      />
      <div className="absolute block w-full p-4">
        <div className="flex flex-row w-full items-center justify-center space-x-1.5 px-2 lg:hidden">
          <button
            className="hover:shadow-md shadow-sm grid grid-cols-3 w-full rounded-full h-[48px] px-1.5 text-black font-medium text-xs border-[1px] border-gray-light items-center sm:text-sm sm:max-w-xl justify-between"
            onClick={activateMobileSearchModal}
          >
            <div className="w-full border-r-2 border-gray-light px-2 sm:max-w-full overflow-hidden text-ellipsis whitespace-nowrap">
              {extractFirstPhraseBeforeComma(placeName || 'Map Search')}
            </div>

            <div className="w-full border-r-2 border-gray-light px-2  sm:max-w-full overflow-hidden text-ellipsis whitespace-nowrap">
              {checkInDate && checkOutDate
                ? checkInDate.month() == checkOutDate.month()
                  ? `${checkInDate?.format('MMM D')} - ${checkOutDate?.format('D')}`
                  : `${checkInDate?.format('MMM D')} - ${checkOutDate?.format('MMM D')}`
                : 'Add dates'}
            </div>
            <div className="w-full pl-2 pr-4  sm:max-w-full overflow-hidden text-ellipsis whitespace-nowrap">
              {guestDescription(
                adultCount,
                childCount,
                infantCount,
                petCount
              ) || 'Add guests'}
            </div>
          </button>
          <AnalyticsRef name="select_search_filters">
            <ButtonIcon
              icon={IconNames.FILTER}
              iconSize="xs"
              ariaDescribedby="Filters"
              color="primary"
              variant="outlined"
              isCircle={true}
              isFullWidth={true}
              className="focus:outline-none h-9 w-9 mb-0.5"
              data-testid={'@filters/filters-btn'}
              onClick={activateFeaturesModal}
            />
          </AnalyticsRef>
        </div>
      </div>
      {isListActive ? (
        <div
          className={cx(
            'block h-full w-full lg:w-2/3 overflow-hidden max-w-5xl px-4 lg:px-2 mt-16 lg:mt-0',
            {
              hidden: !isListActive,
            }
          )}
        >
          <div className="w-full h-full overflow-hidden">
            <div className="h-full w-full mt-1 pt-2 lg:mt-0 lg:pt-0 px-2 z-50">
              <div className="relative lg:hidden">
                <Divider />
              </div>

              {!listingsLoading &&
                searchedCountry &&
                searchedCountry !== 'US' &&
                listingsQueryResults?.listingsSearch.entries.length === 0 && (
                  <div className="pl-4 sm:pl-8 pt-16 sm:py-16 w-full text-gray-dark">
                    <div className="text-xl font-semibold">NO RESULTS</div>
                    <div className="text-lg font-medium pt-6">
                      Savvy currently focuses on properties and hosts in the
                      United States. While we do have some international
                      destinations, the majority of our listings are currently
                      in the U.S.
                    </div>
                    {/* <div className="text-lg font-medium pt-6">
                      We hope to grow abroad soon.{' '}
                      <Link to={} className="text-primary-main">
                        Click here
                      </Link>{' '}
                      to be notified of our internationalization.
                    </div> */}
                  </div>
                )}
              {!listingsLoading &&
                !zoomingOutToNewListing &&
                searchedCountry &&
                searchedCountry === 'US' &&
                listingsQueryResults?.listingsSearch.entries.length === 0 && (
                  <div className="pl-4 sm:pl-8 pt-16 sm:py-16 w-full text-gray-dark">
                    <div className="text-xl font-semibold">NO RESULTS</div>
                    <div className="text-lg font-medium pt-6">
                      To get more results, try adjusting your search.
                    </div>
                    {checkInDate || checkOutDate || hasFiltersApplied ? (
                      <div>
                        {' '}
                        Unfortunately, there do not appear to be any nearby
                        properties that match your filters.
                      </div>
                    ) : (
                      <div>
                        We are always adding new properties to Savvy. Check back
                        again soon for stays in this area.
                      </div>
                    )}
                    <div className="mt-6 items-center space-y-4 flex flex-col xs:flex-row xs:space-x-4 xs:space-y-0">
                      {isAboveMinZoom && (
                        <Button
                          variant={'outlined'}
                          color={'gray'}
                          className="text-gray-dark border-[1.5px] outline-gray-dark"
                          onClick={() => setZoomingOutToNewListing(true)}
                          disabled={!isAboveMinZoom}
                        >
                          Show nearby properties
                        </Button>
                      )}

                      {(checkInDate || checkOutDate) && (
                        <Button
                          variant={'outlined'}
                          color={'gray'}
                          className="text-gray-dark border-[1.5px] outline-gray-dark"
                          onClick={clearDates}
                        >
                          Clear Dates
                        </Button>
                      )}

                      {hasFiltersApplied && (
                        <Button
                          variant={'outlined'}
                          className="text-gray-dark border-[1.5px] outline-gray-dark"
                          color={'gray'}
                          onClick={resetFilters}
                        >
                          Reset Filters
                        </Button>
                      )}
                    </div>
                  </div>
                )}
            </div>

            {/* Loading state */}
            {listingsLoading && (
              <div className="relative inset-0 min-h-screen sm:min-h-[calc(100vh-80px)] overflow-auto">
                <div className="hidden lg:flex mx-4 mt-5 h-4 bg-gray-xlight rounded w-36" />
                <div className="absolute w-full h-min grid grid-col-1 xs:grid-cols-2 xl:grid-cols-3 pt-4 p-2">
                  {times(30, (index) => (
                    <div
                      key={index}
                      className="animate-pulse flex space-x-4 w-full px-2"
                    >
                      <div className="py-2 space-y-2 flex flex-col w-full">
                        <div className="relative h-64 sm:h-full aspect-[100/95] rounded-xl bg-gray-light" />
                        <div className="space-y-2 py-1">
                          <div className="h-4 bg-gray-xlight rounded w-36" />
                          <div className="h-2 bg-gray-xlight rounded w-20" />
                          <div className="h-2 bg-gray-xlight rounded w-24" />
                          <div className="hidden sm:block h-4 bg-gray-xlight rounded w-48" />
                          <div className="block sm:hidden h-4 bg-gray-xlight rounded w-72" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Results */}
            {!listingsLoading &&
              listingsQueryResults &&
              listingsQueryResults?.listingsSearch.entries.length > 0 && (
                <div
                  ref={resultsContainerRef}
                  style={{
                    zIndex: 1,
                    visibility: shouldShowContent ? 'visible' : 'hidden',
                    opacity: shouldShowContent ? 1 : 0,
                    transition: 'opacity 0.1s ease-in-out',
                  }}
                  className="relative inset-0 min-h-[calc(100vh-72px)] lg:min-h-[calc(100vh-81px)] overflow-auto bg-white"
                >
                  {listingsQueryResults.listingsSearch.metadata.totalCount && (
                    <div className="hidden lg:flex px-4 pt-[18px] flex-row items-center justify-between relative inset-0 overflow-auto">
                      <div className="flex flex-col ">
                        <Typography
                          variant="subtitle2"
                          variantM="subtitle2"
                          component="h5"
                          className="pt-4 sm:pt-0 text-black"
                        >
                          {listingsLoading || !listingsQueryResults ? (
                            <div className="animate-pulse flex space-x-4">
                              <div className="flex-1 flex justify-end space-y-6">
                                <div className="h-4 bg-gray-light rounded w-48" />
                              </div>
                            </div>
                          ) : (
                            <div className="flex flex-row justify-between">
                              <div className="flex justify-end pl-1 text-primary-main">
                                {propertyCountFormattedString(
                                  listingsQueryResults.listingsSearch.metadata
                                    .totalCount
                                )}
                              </div>
                              <Link
                                to={routes.about}
                                target="_blank"
                                rel="noreferrer"
                                className="a pl-1 text-primary-main"
                              >
                                without service fees*
                              </Link>
                            </div>
                          )}
                        </Typography>
                      </div>
                    </div>
                  )}

                  <AnalyticsRef
                    name="search_results"
                    data-page-size={searchPageSize}
                    data-testid={'@search/search-results'}
                  >
                    <div className="absolute top-0 lg:top-10 w-full">
                      <div className="grid grid-cols-1 px-4 sm:grid-cols-2 sm:px-2 xl:grid-cols-3 gap-2 sm:gap-2">
                        {listingsQueryResults?.listingsSearch.entries.map(
                          (listing, index) => (
                            <div
                              key={listing.slug}
                              className="mb-6 sm:mb-0"
                              onMouseEnter={(): void =>
                                setHoveredListingSlug(listing.slug)
                              }
                              onMouseLeave={(): void =>
                                setHoveredListingSlug(undefined)
                              }
                              onClick={saveScrollPosition}
                            >
                              <ListingCard
                                listing={listing}
                                altTag={'temp'}
                                accountShowRoute={routes.accounts(
                                  listing.account.slug || ''
                                )}
                                avatarUrl={listing.account.avatarUrl}
                                isFavorited={listing.isFavorited}
                                displayStay={!checkInDate && !checkOutDate}
                                showRoute={routes.listings({
                                  slug: listing.slug,
                                  checkInDate: checkInDate
                                    ? formatDateYYYYMMDD(checkInDate)
                                    : listing.priceDisplay?.checkInDate
                                      ? formatDateYYYYMMDD(
                                          moment(
                                            listing.priceDisplay.checkInDate
                                          )
                                        )
                                      : undefined,
                                  checkOutDate: checkOutDate
                                    ? formatDateYYYYMMDD(checkOutDate)
                                    : listing.priceDisplay?.checkOutDate
                                      ? formatDateYYYYMMDD(
                                          moment(
                                            listing.priceDisplay.checkOutDate
                                          )
                                        )
                                      : undefined,
                                  adultCount: adultCount,
                                  childCount: childCount,
                                  infantCount: infantCount,
                                  petCount: petCount,
                                })}
                                priority={index <= 9}
                                onFavoritedSuccess={(isNowFavorited) => {
                                  {
                                    selectedListing &&
                                      selectedListing.id === listing.id &&
                                      updateSelectedListing(
                                        selectedListing,
                                        isNowFavorited
                                      );
                                  }
                                }}
                              />
                            </div>
                          )
                        )}
                      </div>
                      {listingsQueryResults.listingsSearch.metadata
                        .totalCount &&
                        listingsQueryResults.listingsSearch.metadata
                          .totalCount > searchPageSize && (
                          <div className="relative bottom-0 block bg-white h-40">
                            <nav className="flex absolute bottom-24 lg:bottom-14 w-full text-center justify-center space-x-4">
                              <div
                                className={cx({
                                  'cursor-not-allowed':
                                    !listingsQueryResults.listingsSearch
                                      .metadata.cursorBefore,
                                })}
                              >
                                <Button
                                  onClick={() => {
                                    prevPage();
                                    scrollToTop();
                                  }}
                                  variant="outlined"
                                  color="gray"
                                  size="sm"
                                  disabled={
                                    !listingsQueryResults.listingsSearch
                                      .metadata.cursorBefore
                                  }
                                >
                                  Previous
                                </Button>
                              </div>

                              <div
                                className={cx({
                                  'cursor-not-allowed':
                                    !listingsQueryResults.listingsSearch
                                      .metadata.cursorAfter,
                                })}
                              >
                                <Button
                                  onClick={() => {
                                    nextPage();
                                    scrollToTop();
                                  }}
                                  variant="outlined"
                                  color="gray"
                                  size="sm"
                                  disabled={
                                    !listingsQueryResults.listingsSearch
                                      .metadata.cursorAfter
                                  }
                                >
                                  Next
                                </Button>
                              </div>
                            </nav>
                          </div>
                        )}

                      {/* if there is less than a full page of listings,
                      and the user is not too far zoomed out,
                      show the nearby properties button */}
                      {totalCount <= searchPageSize && isAboveMinZoom && (
                        <div className="block sm:pl-8 pb-40 sm:pb-16 w-full text-gray-dark">
                          <div className="text-sm text-center sm:text-left font-medium pt-4">
                            There may be more results. You can try
                          </div>
                          <div className="mt-6 items-center space-y-4 flex flex-col xs:flex-row xs:space-x-4 xs:space-y-0">
                            <Button
                              variant={'outlined'}
                              color={'gray'}
                              className="text-gray-dark border-[1.5px] outline-gray-dark"
                              onClick={() => setZoomingOutToNewListing(true)}
                              size={'sm'}
                            >
                              Show nearby properties
                            </Button>

                            {(checkInDate || checkOutDate) && (
                              <Button
                                variant={'outlined'}
                                color={'gray'}
                                className="text-gray-dark border-[1.5px] outline-gray-dark"
                                onClick={clearDates}
                                size={'sm'}
                              >
                                Clear Dates
                              </Button>
                            )}

                            {hasFiltersApplied && (
                              <Button
                                variant={'outlined'}
                                className="text-gray-dark border-[1.5px] outline-gray-dark"
                                color={'gray'}
                                onClick={resetFilters}
                                size={'sm'}
                              >
                                Reset Filters
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </AnalyticsRef>
                </div>
              )}
          </div>
        </div>
      ) : null}
      {!isListActive && (
        <div className="pt-12 w-full sm:pt-2 sm:h-screen bg-white px-4 sm:px-6">
          <div className="hidden lg:flex" />
        </div>
      )}
      <div
        className={cx(
          'lg:top-[32px] right-0 bottom-0 w-full max-h-[calc(100vh-80px)] googlemap:w-auto sm:mt-12 lg:pt-0 border-t border-gray-light',
          {
            'top-[78px] sm:top-[32px] h-[calc(100vh-72px)] lg:h-[calc(100vh-80px)] lg:w-full min-[1600px]:w-full googlemap:w-full googlemap:left-0 fixed lg:block':
              !isListActive,
          },
          {
            'hidden absolute sm:h-[calc(100vh-72px)] lg:block lg:w-1/3 min-[1536px]:w-[calc(100vw-1024px)] googlemap:left-1024px':
              isListActive,
          }
        )}
      >
        <div className="absolute inset-0" data-testid={'@map/map-area'}>
          <Wrapper apiKey={apiKey}>
            {centerPoint && mapZoom && (
              <Map
                style={{
                  height: '100% ',
                  width: '100%',
                  outline: 'none',
                }}
                center={centerPoint}
                zoom={mapZoom}
                gestureHandling="greedy"
                clickableIcons={false}
                disableDefaultUI={true}
                zoomControl={true}
                zoomControlOptions={{
                  // Bottom Right
                  position: 9.0,
                }}
                onDragEnd={debouncedMapChangeHandler}
                onZoomChanged={debouncedMapChangeHandler}
                onResize={debouncedMapChangeHandler}
                onClick={mapClicked}
                minZoom={2}
                maxZoom={17}
              >
                {mapListings.map((listing, index) => {
                  if (listing.lat && listing.lng) {
                    return (
                      <MapMarker
                        key={listing.slug}
                        hover={listing.slug === hoveredListingSlug}
                        zIndex={listing.slug === hoveredListingSlug ? 2 : 1}
                        icon={{
                          ...MapMarkerDefaultIcon,
                          ...{
                            fillColor:
                              listing.slug === hoveredListingSlug ||
                              listing.slug === selectedListing?.slug
                                ? palette.gray.main
                                : palette.primary.main,
                            scale:
                              listing.slug === hoveredListingSlug ||
                              listing.slug === selectedListing?.slug
                                ? 14
                                : 9,
                          },
                        }}
                        data-testid={concatenateAndReplace(
                          `${listing.name}-map`
                        )}
                        position={{ lat: listing.lat, lng: listing.lng }}
                        onClick={() => {
                          setSelectedListingId(listing.id);
                        }}
                        priceLabel={
                          listing.priceDisplay?.baseAmountCents &&
                          listing.priceDisplay?.nights
                            ? formatWholeDollarAmount(
                                toInteger(
                                  listing.priceDisplay.baseAmountCents /
                                    listing.priceDisplay.nights
                                )
                              )
                            : undefined
                        }
                        index={index}
                      />
                    );
                  } else {
                    return null;
                  }
                })}
                {selectedListing &&
                  selectedListing.lat &&
                  selectedListing.lng && (
                    <MapOverlay
                      position={{
                        lat: (() => {
                          // Ensure the card stays within the map's vertical bounds (concise pixel-based)
                          const bounds = map?.getBounds();
                          const projection = map?.getProjection();
                          if (!bounds || !projection || !map)
                            return selectedListing.lat;

                          const CARD_HEIGHT = 325; // px, matches your card's actual height
                          const CARD_OFFSET_Y = 60; // px, vertical offset from marker to card
                          const mapHeight = map.getDiv().offsetHeight;
                          const scale = Math.pow(2, map.getZoom() || 0);

                          const markerLatLng = new google.maps.LatLng(
                            selectedListing.lat,
                            selectedListing.lng
                          );
                          const markerPoint =
                            projection.fromLatLngToPoint(markerLatLng);
                          const nePoint = projection.fromLatLngToPoint(
                            bounds.getNorthEast()
                          );
                          if (!markerPoint || !nePoint)
                            return selectedListing.lat;

                          let y = (markerPoint.y - nePoint.y) * scale;
                          const cardTop = y - CARD_OFFSET_Y;
                          const cardBottom = cardTop + CARD_HEIGHT;

                          if (cardTop < 0) y += -cardTop;
                          else if (cardBottom > mapHeight)
                            y -= cardBottom - mapHeight;

                          const adjustedPoint = new google.maps.Point(
                            markerPoint.x,
                            nePoint.y + y / scale
                          );
                          return (
                            projection
                              .fromPointToLatLng(adjustedPoint)
                              ?.lat() || selectedListing.lat
                          );
                        })(),
                        lng: (() => {
                          // Ensure the card stays within the map's horizontal bounds
                          const bounds = map?.getBounds();
                          if (!bounds || !map) return selectedListing.lng;

                          const projection = map.getProjection();
                          if (!projection) return selectedListing.lng;

                          // Card dimensions and positioning
                          const CARD_WIDTH = 325;
                          const CARD_OFFSET_X = 144;
                          const mapWidth = map.getDiv().offsetWidth;

                          // Convert marker position to pixel coordinates
                          const markerPoint = projection.fromLatLngToPoint(
                            new google.maps.LatLng(
                              selectedListing.lat,
                              selectedListing.lng
                            )
                          );
                          const swPoint = projection.fromLatLngToPoint(
                            bounds.getSouthWest()
                          );
                          if (!markerPoint || !swPoint)
                            return selectedListing.lng;

                          // Calculate card position in pixels
                          const scale = Math.pow(2, map.getZoom() || 0);
                          const x = (markerPoint.x - swPoint.x) * scale;
                          const cardRightEdge = x + CARD_WIDTH - CARD_OFFSET_X;
                          const cardLeftEdge = x - CARD_OFFSET_X;

                          // Adjust position if card would extend beyond map edges
                          if (cardRightEdge > mapWidth) {
                            const adjustedX =
                              (mapWidth - CARD_WIDTH + CARD_OFFSET_X - 10) /
                              scale;
                            return (
                              projection
                                .fromPointToLatLng(
                                  new google.maps.Point(
                                    swPoint.x + adjustedX,
                                    markerPoint.y
                                  )
                                )
                                ?.lng() || selectedListing.lng
                            );
                          }

                          if (cardLeftEdge < 0) {
                            const adjustedX = (CARD_OFFSET_X + 10) / scale;
                            return (
                              projection
                                .fromPointToLatLng(
                                  new google.maps.Point(
                                    swPoint.x + adjustedX,
                                    markerPoint.y
                                  )
                                )
                                ?.lng() || selectedListing.lng
                            );
                          }

                          return selectedListing.lng;
                        })(),
                      }}
                    >
                      <div className="w-[325px] hidden lg:block">
                        <ListingMapCard
                          listing={selectedListing}
                          isFavorited={selectedListing.isFavorited}
                          closeMapCard={onMapCardCloseHandler}
                          onFavoritedSuccess={(isNowFavorited) => {
                            updateSelectedListing(
                              selectedListing,
                              isNowFavorited
                            );
                          }}
                          listingRoute={routes.listings({
                            slug: selectedListing.slug,
                            adultCount: adultCount,
                            childCount: childCount,
                            infantCount: infantCount,
                            petCount: petCount,
                            checkInDate: checkInDate?.format('YYYY-MM-DD'),
                            checkOutDate: checkOutDate?.format('YYYY-MM-DD'),
                          })}
                        />
                      </div>
                    </MapOverlay>
                  )}
              </Map>
            )}
          </Wrapper>
        </div>
      </div>
      <div className="bg-white block fixed right-1/2 bottom-0 -translate-y-8 translate-x-1/2 shadow-md lg:hidden z-50 rounded-full">
        <Button
          onClick={() => {
            if (isListActive && resultsContainerRef.current) {
              savePosition(resultsContainerRef.current.scrollTop);
            }
            setIsListActive(!isListActive);
          }}
          variant="outlined"
          color="gray"
          size="sm"
          data-testid={`@search/${isListActive ? 'HideList' : 'ShowList'}-btn`}
        >
          {isListActive ? 'Show Map' : 'Show List'}
        </Button>
      </div>
      {selectedListing && !isListActive && (
        <div className="fixed flex max-w-[400px] px-4 aspect-3 right-1/2 bottom-0 -translate-y-24 translate-x-1/2 lg:hidden z-50 w-full min-[400px]:px-0">
          <ListingMapCard
            listing={selectedListing}
            isFavorited={selectedListing.isFavorited}
            closeMapCard={onMapCardCloseHandler}
            onFavoritedSuccess={(isNowFavorited) => {
              updateSelectedListing(selectedListing, isNowFavorited);
            }}
            listingRoute={routes.listings({
              slug: selectedListing.slug,
              adultCount: adultCount,
              childCount: childCount,
              infantCount: infantCount,
              petCount: petCount,
              checkInDate: checkInDate?.format('YYYY-MM-DD'),
              checkOutDate: checkOutDate?.format('YYYY-MM-DD'),
            })}
          />
        </div>
      )}
    </div>
  );
};
